import { ReactNode } from 'react';

/**
 * OmniRetail — Option A primitives
 *
 * Kept API-compatible with the original common.tsx so the 40+ pages
 * already built don't need changes. Visual treatment replaced to match
 * the Shopify-inspired design language.
 */

// ============================================================
// PageHeader — editorial hierarchy with optional eyebrow + actions
// ============================================================
export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between mb-8 gap-4">
      <div className="min-w-0">
        {eyebrow && (
          <div className="eyebrow mb-2 text-ink-500">{eyebrow}</div>
        )}
        <h1 className="text-3xl font-bold text-ink-900 text-balance">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-ink-500 mt-1.5 max-w-2xl">{subtitle}</p>
        )}
      </div>
      {actions && (
        <div className="flex gap-2 flex-shrink-0 pt-1">{actions}</div>
      )}
    </div>
  );
}

// ============================================================
// StubBanner — kept for legacy stub pages (sage accent instead of amber)
// ============================================================
export function StubBanner({ frIds, description }: { frIds: string[]; description: string }) {
  return (
    <div className="bg-sage-50 border border-sage-200 text-sage-900 rounded-xl p-4 mb-6">
      <div className="font-semibold mb-1 text-sm">Preview feature</div>
      <div className="text-sm text-ink-700">{description}</div>
      <div className="mt-2 font-mono text-2xs text-ink-500">{frIds.join(' · ')}</div>
    </div>
  );
}

// ============================================================
// EmptyState — illustrated, with optional CTA
// ============================================================
export function EmptyState({
  message,
  hint,
  action,
  icon,
}: {
  message: string;
  hint?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="text-center py-16 px-6">
      <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-canvas-subtle flex items-center justify-center text-ink-400">
        {icon ?? <EmptyIcon />}
      </div>
      <div className="text-ink-900 font-semibold mb-1">{message}</div>
      {hint && <div className="text-sm text-ink-500 max-w-sm mx-auto">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

function EmptyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ============================================================
// LoadingState — subtle pulsing placeholder
// ============================================================
export function LoadingState({ rows = 3 }: { rows?: number }) {
  return (
    <div className="p-6 space-y-3" role="status" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-4 bg-canvas-subtle rounded-md animate-pulse"
          style={{ width: `${70 + (i * 7) % 25}%`, animationDelay: `${i * 100}ms` }}
        />
      ))}
    </div>
  );
}

// ============================================================
// ErrorState
// ============================================================
export function ErrorState({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="card border-danger/20 bg-red-50/30 p-5">
      <div className="flex gap-3 items-start">
        <div className="w-8 h-8 rounded-full bg-danger/10 flex items-center justify-center flex-shrink-0 text-danger">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01" strokeLinecap="round"/>
          </svg>
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-ink-900 text-sm">Something went wrong</div>
          <div className="text-sm text-ink-700 mt-0.5 break-words">{message}</div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// StatusBadge — semantic color mapping with dot
// ============================================================
type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'sage';
const TONE_CLASSES: Record<StatusTone, string> = {
  success: 'bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200',
  sage:    'bg-sage-50 text-sage-700 ring-1 ring-inset ring-sage-200',
  warning: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200',
  danger:  'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  info:    'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
  neutral: 'bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200',
};
const TONE_DOT: Record<StatusTone, string> = {
  success: 'bg-sage-500',
  sage:    'bg-sage-500',
  warning: 'bg-amber-500',
  danger:  'bg-danger',
  info:    'bg-info',
  neutral: 'bg-ink-400',
};

export function StatusBadge({
  status,
  tone,
  dot = true,
}: {
  status: string;
  tone?: StatusTone;
  dot?: boolean;
}) {
  const resolvedTone = tone ?? inferTone(status);
  return (
    <span className={`badge ${TONE_CLASSES[resolvedTone]}`}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${TONE_DOT[resolvedTone]}`} />}
      {status}
    </span>
  );
}

function inferTone(status: string): StatusTone {
  const s = status.toLowerCase();
  if (['active', 'approved', 'posted', 'paid', 'completed', 'success', 'ok'].includes(s)) return 'success';
  if (['pending', 'draft', 'under_review', 'queued', 'processing'].includes(s)) return 'warning';
  if (['inactive', 'cancelled', 'rejected', 'failed', 'error', 'void', 'overdue'].includes(s)) return 'danger';
  if (['info', 'testing', 'new'].includes(s)) return 'info';
  return 'neutral';
}

// ============================================================
// KpiCard — summary strip tile with accent bar
// ============================================================
const KPICARD_ACCENTS: Record<string, { bar: string; icon: string; value: string }> = {
  sage:    { bar: 'bg-sage-500',    icon: 'bg-sage-100 text-sage-600',    value: 'text-sage-700' },
  amber:   { bar: 'bg-amber-500',   icon: 'bg-amber-100 text-amber-700',  value: 'text-amber-800' },
  danger:  { bar: 'bg-danger',      icon: 'bg-red-100 text-red-600',      value: 'text-red-700' },
  info:    { bar: 'bg-info',        icon: 'bg-blue-100 text-blue-600',    value: 'text-blue-700' },
  neutral: { bar: 'bg-ink-300',     icon: 'bg-canvas-subtle text-ink-500', value: 'text-ink-900' },
};

export function KpiCard({
  label,
  value,
  delta,
  trend,
  icon,
  accent = 'neutral',
}: {
  label: string;
  value: string | number;
  delta?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: ReactNode;
  accent?: 'sage' | 'amber' | 'danger' | 'info' | 'neutral';
}) {
  const tones = KPICARD_ACCENTS[accent] ?? KPICARD_ACCENTS.neutral;
  const trendColor = trend === 'up' ? 'text-sage-600' : trend === 'down' ? 'text-danger' : 'text-ink-500';
  return (
    <div className="card p-5 relative overflow-hidden">
      {/* Accent bar — top left vertical stripe */}
      <div className={`absolute top-0 left-0 bottom-0 w-1 ${tones.bar} rounded-l-xl`} />
      <div className="pl-3 flex items-start justify-between gap-3">
        <div className="eyebrow text-ink-500">{label}</div>
        {icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${tones.icon}`}>
            {icon}
          </div>
        )}
      </div>
      <div className={`pl-3 mt-3 text-3xl font-bold font-display tracking-tight ${tones.value}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {delta && (
        <div className={`pl-3 mt-1.5 text-xs font-semibold ${trendColor}`}>
          {trend === 'up' && '↑ '}
          {trend === 'down' && '↓ '}
          {delta}
        </div>
      )}
    </div>
  );
}

// ============================================================
// SectionHeader — inline section titles within a page
// ============================================================
export function SectionHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-end justify-between mb-4 gap-3">
      <div>
        <h2 className="text-lg font-semibold font-display text-ink-900">{title}</h2>
        {description && <p className="text-sm text-ink-500 mt-0.5">{description}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}
